/* import __COLOCATED_TEMPLATE__ from './superman-graph.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import * as d3 from 'd3';

/**
 * Superman Graph Component
 *
 * Displays a line chart comparing all racers to a virtual "Superman"
 * who has the fastest split at each timing point. Racers are shown
 * "falling off" Superman's pace as they accumulate time behind.
 *
 * @param {Array} graphData - Array of racer objects with points array
 * @param {Array} xAxisLabels - Labels for x-axis (Start, TP1, TP2, ..., Finish)
 * @param {String} classGroupName - Name of the class group
 */
export default class SupermanGraphComponent extends Component {
  @tracked lastUpdateTime = 0;
  @tracked _updateScheduled = false;

  svgElement = null;
  previousDataJson = null;

  // Color scale for different racers
  colorScale = d3.scaleOrdinal(d3.schemeTableau10);

  /**
   * Format milliseconds to readable time string
   */
  formatTime(ms) {
    if (ms === 0) return '0:00';
    const isNegative = ms < 0;
    const absMs = Math.abs(ms);
    const seconds = Math.floor(absMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    const formatted = minutes > 0
      ? `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
      : `0:${remainingSeconds.toString().padStart(2, '0')}`;

    return isNegative ? `-${formatted}` : `+${formatted}`;
  }

  /**
   * Check if we should update based on throttle (5 second minimum)
   */
  shouldThrottleUpdate() {
    const now = Date.now();
    return (now - this.lastUpdateTime) < 5000;
  }

  /**
   * Schedule an update with throttling
   */
  scheduleUpdate() {
    if (this._updateScheduled) return;

    const timeSinceUpdate = Date.now() - this.lastUpdateTime;
    const delay = Math.max(0, 5000 - timeSinceUpdate);

    this._updateScheduled = true;
    setTimeout(() => {
      this._updateScheduled = false;
      this.lastUpdateTime = Date.now();
      if (this.svgElement) {
        this.renderGraph(this.svgElement);
      }
    }, delay);
  }

  @action
  setupGraph(element) {
    this.svgElement = element;
    this.lastUpdateTime = Date.now();
    this.renderGraph(element);
  }

  @action
  updateGraph(element) {
    // Check if data has actually changed
    const currentDataJson = JSON.stringify(this.args.graphData);
    if (currentDataJson === this.previousDataJson) {
      return; // No change, skip update
    }

    // Throttle updates
    if (this.shouldThrottleUpdate()) {
      this.scheduleUpdate();
      return;
    }

    this.previousDataJson = currentDataJson;
    this.lastUpdateTime = Date.now();
    this.renderGraph(element);
  }

  renderGraph(element) {
    const { graphData, xAxisLabels } = this.args;

    if (!graphData || !graphData.length || !xAxisLabels || !xAxisLabels.length) {
      return;
    }

    // Clear existing content
    d3.select(element).selectAll('*').remove();

    // Count finished racers for height calculation
    const finishedRacers = graphData.filter(r => r.isFinished);
    const labelHeight = 14; // Pixels per label
    const minLabelSpace = finishedRacers.length * labelHeight;

    // Dimensions - scale height based on number of finishers
    const containerWidth = element.parentElement?.clientWidth || 800;
    const margin = { top: 40, right: 200, bottom: 60, left: 80 }; // More right margin for labels
    const width = Math.max(600, containerWidth) - margin.left - margin.right;

    // Height: minimum 400px, or enough for all labels plus padding
    const minHeight = 400;
    const height = Math.max(minHeight, minLabelSpace + 100);

    // Create SVG
    const svg = d3.select(element)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // X scale - point scale for timing points
    const xScale = d3.scalePoint()
      .domain(xAxisLabels)
      .range([0, width])
      .padding(0.5);

    // Y scale - time behind (0 at top, increasing downward)
    const maxBehind = d3.max(graphData, d => d3.max(d.points, p => p.behind)) || 60000;

    // Ensure y-scale has enough range for label spacing
    // If labels would overlap, expand the domain
    const minYRange = minLabelSpace / height * maxBehind;
    const adjustedMaxBehind = Math.max(maxBehind * 1.1, minYRange * 1.2);

    const yScale = d3.scaleLinear()
      .domain([0, adjustedMaxBehind])
      .range([0, height]);

    // X axis
    svg.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    // Y axis with time formatting
    const yAxis = d3.axisLeft(yScale)
      .ticks(10)
      .tickFormat(d => this.formatTime(d));

    svg.append('g')
      .attr('class', 'y-axis')
      .call(yAxis);

    // Y axis label
    svg.append('text')
      .attr('class', 'y-axis-label')
      .attr('transform', 'rotate(-90)')
      .attr('y', -60)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .text('Time Behind Superman');

    // Superman line at y=0
    svg.append('line')
      .attr('class', 'superman-line')
      .attr('x1', 0)
      .attr('y1', yScale(0))
      .attr('x2', width)
      .attr('y2', yScale(0))
      .attr('stroke', '#ffd700')
      .attr('stroke-width', 3)
      .attr('stroke-dasharray', '5,5');


    // Line generator
    const line = d3.line()
      .defined(d => d.behind !== null && d.behind !== undefined)
      .x(d => xScale(d.timingPointName))
      .y(d => yScale(d.behind))
      .curve(d3.curveMonotoneX);

    // Create tooltip
    const tooltip = d3.select('body').append('div')
      .attr('class', 'superman-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background-color', 'rgba(0,0,0,0.8)')
      .style('color', 'white')
      .style('padding', '8px 12px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('z-index', '1000')
      .style('pointer-events', 'none');

    // Draw lines for each racer
    graphData.forEach((racer, i) => {
      const color = this.colorScale(i);

      // Filter points that have valid x-axis positions
      const validPoints = racer.points.filter(p => xScale(p.timingPointName) !== undefined);

      if (validPoints.length < 2) return;

      // Draw line
      svg.append('path')
        .datum(validPoints)
        .attr('class', 'racer-line')
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', racer.place && racer.place <= 3 ? 3 : 1.5)
        .attr('stroke-opacity', racer.isFinished ? 1 : 0.5)
        .attr('d', line);

      // Draw points
      svg.selectAll(`.point-${i}`)
        .data(validPoints)
        .enter()
        .append('circle')
        .attr('class', `point-${i}`)
        .attr('cx', d => xScale(d.timingPointName))
        .attr('cy', d => yScale(d.behind))
        .attr('r', 4)
        .attr('fill', color)
        .attr('stroke', 'white')
        .attr('stroke-width', 1)
        .on('mouseover', (event, d) => {
          tooltip
            .style('visibility', 'visible')
            .html(`
              <strong>${racer.name}</strong> (Bib: ${racer.bib})<br/>
              ${d.timingPointName}: ${this.formatTime(d.behind)}
            `);
        })
        .on('mousemove', (event) => {
          tooltip
            .style('top', (event.pageY - 10) + 'px')
            .style('left', (event.pageX + 10) + 'px');
        })
        .on('mouseout', () => {
          tooltip.style('visibility', 'hidden');
        });

    });

    // Collect all label positions for finished racers, then adjust to avoid overlap
    const labels = [];
    graphData.forEach((racer, i) => {
      if (racer.isFinished) {
        const validPoints = racer.points.filter(p => xScale(p.timingPointName) !== undefined);
        if (validPoints.length > 0) {
          const lastPoint = validPoints[validPoints.length - 1];
          labels.push({
            racer,
            color: this.colorScale(i),
            x: xScale(lastPoint.timingPointName) + 8,
            y: yScale(lastPoint.behind),
            originalY: yScale(lastPoint.behind),
            text: `${racer.place ? racer.place + '. ' : ''}${racer.name}`
          });
        }
      }
    });

    // Sort labels by y position and adjust overlapping ones
    labels.sort((a, b) => a.y - b.y);
    const minLabelGap = 12; // Minimum pixels between labels

    for (let i = 1; i < labels.length; i++) {
      const prev = labels[i - 1];
      const curr = labels[i];
      if (curr.y - prev.y < minLabelGap) {
        curr.y = prev.y + minLabelGap;
      }
    }

    // Draw labels with adjusted positions
    labels.forEach(label => {
      // Draw connector line if label was moved significantly
      if (Math.abs(label.y - label.originalY) > 2) {
        svg.append('line')
          .attr('x1', label.x - 4)
          .attr('y1', label.originalY)
          .attr('x2', label.x - 2)
          .attr('y2', label.y)
          .attr('stroke', label.color)
          .attr('stroke-width', 0.5)
          .attr('stroke-opacity', 0.5);
      }

      svg.append('text')
        .attr('x', label.x)
        .attr('y', label.y)
        .attr('dy', '0.35em')
        .style('font-size', '10px')
        .style('fill', label.color)
        .text(label.text);
    });

    // Store for cleanup
    this._tooltip = tooltip;
  }

  willDestroy() {
    super.willDestroy();
    // Clean up tooltip
    if (this._tooltip) {
      this._tooltip.remove();
    }
  }
}
